import formConfig from './cpf';
import { cpfClusters } from '../pages/cpf/coverSheet';
import { cpfMetrics } from '../pages/cpf/metrics';
import { metricQuestions } from '../pages/cpf/monthlyMetrics';
import {
  buildDynamicTablesWorkbook,
  importDynamicTableSheetsFromExcel,
  type DynamicTableExcelOptions,
} from '../../utils/dynamic-table-excel';
import { validateDynamicTableCells } from '../../utils/dynamic-table-validation';
import {
  getEnabledSubsections,
  getLockedSections,
  getLockedSubsections,
} from '../../utils/config-utils';

const tables: DynamicTableExcelOptions[] = metricQuestions.map((question) => ({
  title: question.title,
  rowHeader: 'Metric',
  rows: question.dynamicTable!.rows,
  columns: question.dynamicTable!.columns,
  values: Object.fromEntries(
    question.dynamicTable!.rows.flatMap((row) => [
      [`${row.key}month1`, '0'],
      [`${row.key}month2`, row.allowDecimal ? '12.5' : '12'],
      [`${row.key}month3`, ''],
      [`${row.key}notes`, 'Month 3 is not yet available'],
    ]),
  ),
  interpolationValues: {},
}));

describe('CPF first version', () => {
  it('asks for reporting period, email, full name and department in order, with full-length tables', () => {
    const setup = formConfig.sections[0].subsections[0];
    expect(setup.title).toBe('Department details');
    const page = setup.pages.find(({ name }) => name === 'return-details')!;
    expect(page.title).toBe('Department details');
    expect('questions' in page && page.questions?.map(({ name }) => name)).toEqual([
      'report-period',
      'contact-email',
      'contact-name',
      'department',
    ]);
    for (const question of metricQuestions) expect(question.dynamicTable?.height).toBeUndefined();
  });

  it('locks metrics until every setup answer is supplied and setup is no longer a draft', () => {
    const answers = {
      'report-period': JSON.stringify({ quarter: 'Quarter 1 (April-June)', year: '2026-27' }),
      'contact-email': 'jane.smith@example.gov.uk',
      'contact-name': 'Jane Smith',
      department: 'Cabinet Office (CO)',
      cluster: 'Matrix',
    };
    for (const field of Object.keys(answers)) {
      for (const value of ['', '   ', undefined]) {
        const data = { answers: { ...answers, [field]: value } };
        const enabled = getEnabledSubsections(formConfig, data);
        expect(getLockedSections(formConfig.sections, enabled, data)['performance-data']).toBe(
          true,
        );
        expect(getLockedSubsections(formConfig.sections, enabled, data).metrics).toBe(true);
      }
    }
    for (const locked of [true, false]) {
      const data = { answers, drafts: { setup: locked ? ['return-details'] : [] } };
      const enabled = getEnabledSubsections(formConfig, data);
      expect(getLockedSections(formConfig.sections, enabled, data)['performance-data']).toBe(
        locked,
      );
      expect(getLockedSubsections(formConfig.sections, enabled, data).metrics).toBe(locked);
    }
  });

  it('loads and serialises the review tenant without publishing generated config', () => {
    expect(JSON.parse(JSON.stringify(formConfig))).toEqual(formConfig);
    expect(formConfig).toMatchObject({ id: 'cpf', live: false, confirmationEmail: true });
    expect(Object.values(cpfClusters).flat()).toHaveLength(17);
    expect(cpfClusters.Matrix).toContain('CO');
    expect(cpfClusters.Defence).toEqual(['MOD Mil Pers']);
    expect(cpfClusters.Unity).toEqual(['HMRC', 'DfT', 'MHCLG']);
  });

  it('covers every supplied metric once, with three months and adjacent notes', () => {
    expect(cpfMetrics).toHaveLength(74);
    expect(new Set(cpfMetrics.map(({ code }) => code)).size).toBe(74);
    const rows = tables.flatMap((table) => table.rows);
    expect(rows.map(({ key }) => key).sort()).toEqual(
      cpfMetrics.map(({ code }) => code.toLowerCase()).sort(),
    );
    expect(rows.find(({ key }) => key === 'p2p13')?.label).toContain('invoices were received');
    expect(rows.some(({ key }) => key === 'p2p1')).toBe(false);
    for (const table of tables) {
      expect(table.columns.map(({ key }) => key)).toEqual(['month1', 'month2', 'month3', 'notes']);
      expect(validateDynamicTableCells(table.rows, table.columns, table.values)).toBe(true);
      expect(validateDynamicTableCells(table.rows, table.columns, {})).toContain('missing data');
    }
  });

  it('round trips all 74 metrics, decimals, zero and missing-data notes in one workbook', async () => {
    const options = { title: 'CPF monthly metrics', tables };
    const workbook = await buildDynamicTablesWorkbook(options);
    expect(workbook.worksheets).toHaveLength(12);
    const buffer = await workbook.xlsx.writeBuffer();
    const file = { name: 'cpf.xlsx', arrayBuffer: async () => buffer } as File;
    await expect(importDynamicTableSheetsFromExcel(file, options)).resolves.toEqual(
      tables.map((table) => table.values),
    );
  });

  it.each([
    ['Quarter 1 (April-June)', '2026-27', ['Apr 26', 'May 26', 'Jun 26']],
    ['Quarter 2 (July-September)', '2026-27', ['Jul 26', 'Aug 26', 'Sep 26']],
    ['Quarter 3 (October-December)', '2026-27', ['Oct 26', 'Nov 26', 'Dec 26']],
    ['Quarter 4 (January-March)', '2025-26', ['Jan 26', 'Feb 26', 'Mar 26']],
  ])('uses selected %s %s for workbook headings and import', async (quarter, year, months) => {
    const options = {
      title: 'CPF',
      tables: tables.map((table) => ({
        ...table,
        interpolationValues: { 'report-period': JSON.stringify({ quarter, year }) },
      })),
    };
    const workbook = await buildDynamicTablesWorkbook(options);
    for (const sheet of workbook.worksheets) {
      expect(['B3', 'C3', 'D3'].map((cell) => sheet.getCell(cell).value)).toEqual(months);
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const file = { name: 'cpf.xlsx', arrayBuffer: async () => buffer } as File;
    await expect(importDynamicTableSheetsFromExcel(file, options)).resolves.toEqual(
      tables.map((table) => table.values),
    );
    await expect(
      importDynamicTableSheetsFromExcel(file, {
        ...options,
        tables: options.tables.map((table) => ({
          ...table,
          interpolationValues: { 'report-period': JSON.stringify({ quarter, year: '2027-28' }) },
        })),
      }),
    ).rejects.toThrow();
  });

  it.each(['', '   ', '1e3', '0x10', '£12', '12,000', '12abc', '-1', '1.5'])(
    'rejects a missing or invalid count from Excel: %p',
    async (value) => {
      const table = { ...tables[0], rows: [tables[0].rows[0]], values: {} };
      const options = { title: 'CPF', tables: [table] };
      const workbook = await buildDynamicTablesWorkbook(options);
      workbook.worksheets[0].getCell('B5').value = value;
      const buffer = await workbook.xlsx.writeBuffer();
      await expect(
        importDynamicTableSheetsFromExcel(
          { name: 'cpf.xlsx', arrayBuffer: async () => buffer } as File,
          options,
        ),
      ).rejects.toThrow();
    },
  );
});
